/**
 * persistence/settings-registry.js
 *
 * Canonical registry of all Hexatone settings keys.
 *
 * Every key that appears in useQuery's spec, settings/session-defaults, or anywhere
 * else in the persistence layer should have an entry here.
 *
 * ## Tiers
 *
 *   'url'      — synced to URL search params AND localStorage.
 *                Survives browser restart and can be shared as a link.
 *                These are the keys passed to useQuery's spec object.
 *
 *   'session'  — stored in sessionStorage only.
 *                Survives tab refresh but not a new tab/window.
 *                These are the keys restored by settings/session-defaults.js.
 *
 *   'local'    — stored in localStorage only (not in URL).
 *                Survives browser restart but not shareable.
 *                Used for per-controller hardware preferences.
 *
 *   'runtime'  — never stored anywhere.
 *                Always derived from defaults at startup.
 *                Must not be written to any browser storage.
 *
 * ## Types
 *
 *   'int'    — integer, parsed with parseInt
 *   'float'  — float, parsed with parseFloat
 *   'bool'   — boolean, parsed as === "true"
 *   'string' — string, no parsing
 *   'joined' — comma-joined array (ExtractJoinedString)
 *
 * ## Preset skip
 *
 *   presetSkip: true  — key is excluded from URL/localStorage persistence
 *                       (matches PRESET_SKIP_KEYS in hooks/use-presets.js).
 *                       Only populated by explicit preset load or session storage.
 *
 * ## Intentional omissions
 *
 *   octave_offset — The OCT button transpose state is intentionally NOT registered.
 *                   It is reset to 0 on every synth rebuild (output routing change),
 *                   structural rebuild (preset/scale/layout change), and PANIC.
 *                   Persisting it would cause pitch mismatches after these events.
 *                   Implementation: resetOctave() in hooks/use-synth-wiring.js, called
 *                   from useEffect([synth]) and the PANIC button in app.jsx.
 *                   The structuralSettings reset lives in app.jsx useEffect([structuralSettings]).
 *
 *   octave_deferred — Stored directly in sessionStorage by hooks/use-synth-wiring.js
 *                     (key: "octave_deferred"). Not in this registry because it is
 *                     UI interaction state, not a settings value — it controls whether
 *                     the next OCT shift defers to the next note-on rather than
 *                     retuning held notes immediately.
 */

export const SETTINGS_REGISTRY = [
  // ── Preset metadata ─────────────────────────────────────────────────────────
  { key: "name", tier: "url", type: "string", default: "", presetSkip: true },
  { key: "description", tier: "url", type: "string", default: "", presetSkip: true },
  { key: "short_description", tier: "session", type: "string", default: "" },

  // ── Scale / tuning ───────────────────────────────────────────────────────────
  { key: "scale", tier: "url", type: "joined", default: null, presetSkip: true },
  { key: "note_names", tier: "url", type: "joined", default: null, presetSkip: true },
  { key: "note_colors", tier: "url", type: "joined", default: null, presetSkip: true },
  { key: "equivSteps", tier: "url", type: "int", default: 1, presetSkip: true },
  { key: "reference_degree", tier: "url", type: "int", default: 0, presetSkip: true },
  { key: "fundamental", tier: "url", type: "float", default: 440, presetSkip: true },
  { key: "key_colors_mode", tier: "url", type: "string", default: "auto", presetSkip: true },
  { key: "spectrum_colors", tier: "url", type: "bool", default: false, presetSkip: true },
  { key: "auto_colors", tier: "url", type: "bool", default: true, presetSkip: true },
  { key: "fundamental_color", tier: "url", type: "string", default: "#ffdbe8", presetSkip: true },
  { key: "prime_family_colors", tier: "url", type: "joined", default: null, presetSkip: true },
  { key: "key_labels", tier: "url", type: "string", default: "heji", presetSkip: true },
  { key: "show_equaves", tier: "url", type: "bool", default: false, presetSkip: true },
  // HEJI notation anchor: defines the rational offset for the entire spelling.
  // heji_anchor_ratio — the ratio (Scala format, e.g. "3/2" or "702.0") of the
  //   reference pitch whose deviation is 0¢ on a tuning meter.  This is
  //   measured from scale degree 0 (1/1).  It does not need to be a scale
  //   degree; it is a free-form interval that anchors the HEJI frame.
  //   Default "1/1" means the root is the 0¢ reference.
  // heji_anchor_label — the HEJI pitch-class spelling for that pitch (e.g. "nA").
  //   When key_labels === "heji", all degree labels are auto-generated from
  //   this anchor pair + the committed ratio of each degree.
  { key: "heji_anchor_ratio", tier: "url", type: "string", default: "", presetSkip: true },
  { key: "heji_anchor_label", tier: "url", type: "string", default: "", presetSkip: true },
  { key: "heji_anchor_frequency", tier: "url", type: "string", default: "", presetSkip: true },
  { key: "heji_tempered_only", tier: "url", type: "bool", default: false, presetSkip: true },
  // When false, cents deviation is omitted from key labels (still shown in scale table).
  { key: "heji_show_cents", tier: "url", type: "bool", default: false, presetSkip: true },
  // HEJI palette UI state is session-scoped: it survives Settings remounts and
  // page refreshes without becoming part of a tuning preset or share URL.
  { key: "heji_palette_visible", tier: "session", type: "bool", default: false },
  { key: "heji_palette_structure", tier: "session", type: "string", default: "" },
  { key: "heji_palette_deviation", tier: "session", type: "string", default: "" },
  { key: "heji_palette_decimals", tier: "session", type: "int", default: 0 },
  // Internal-only for now: TuneCell supports an alternate reference-degree save
  // mode ('transpose_scale'), but there is no exposed UI toggle yet, so this
  // remains session-scoped and stays out of share URLs.
  { key: "retuning_mode", tier: "session", type: "string", default: "recalculate_reference" },
  { key: "modulation_style", tier: "url", type: "string", default: "fixed_do" },

  // ── Layout / grid ────────────────────────────────────────────────────────────
  { key: "rSteps", tier: "url", type: "int", default: 2, presetSkip: true },
  { key: "drSteps", tier: "url", type: "int", default: 1, presetSkip: true },
  { key: "center_degree", tier: "url", type: "int", default: 0, presetSkip: true },
  { key: "hexSize", tier: "url", type: "int", default: 42, presetSkip: true },
  { key: "rotation", tier: "url", type: "float", default: -16.102113751, presetSkip: true },

  // ── Per-controller local preferences ─────────────────────────────────────────
  // tier: 'local'  — stored in localStorage, not URL-synced, not session-scoped.
  // perController: true  — storage key is `${controllerId}_${key}`.
  //                        Value is loaded into settings on controller connect
  //                        via loadControllerPrefs() in input/controller-anchor.js.
  // perController: false — storage key is the plain key (cross-controller pref).
  //
  // These keys also appear in the 'session' section below as their runtime
  // representation in the settings object. The 'local' entries here describe
  // the *persistence* layer; the session entries describe the *live* state.
  {
    key: "midiin_mpe_input",
    tier: "local",
    type: "bool",
    default: false,
    perController: true,
    description: "Enable MPE input for this controller",
  },
  {
    key: "midiin_bend_flip",
    tier: "local",
    type: "bool",
    default: false,
    perController: true,
    description: "Reverse pitch bend direction for this controller",
  },
  {
    key: "midiin_bend_range",
    tier: "local",
    type: "string",
    default: "28/27",
    perController: false,
    description: "Pitch bend interval (shared across controllers)",
  },
  {
    key: "linnstrument_pitch_bend_mode",
    tier: "local",
    type: "string",
    default: "off",
    perController: true,
    description: "LinnStrument User Firmware pitch bend mode: off or scale-aware row glide",
  },
  {
    key: "linnstrument_pitch_bend_shape",
    tier: "local",
    type: "int",
    default: 60,
    perController: true,
    description: "LinnStrument User Firmware row-glide shaping (linear to near-stepped)",
  },
  {
    key: "linnstrument_x_spike_reduction",
    tier: "local",
    type: "int",
    default: 25,
    perController: true,
    description:
      "LinnStrument User Firmware X-data spike reduction strength (0 = off, 100 = strongest)",
  },
  {
    key: "linnstrument_x_input_smoothing",
    tier: "local",
    type: "int",
    default: 80,
    perController: true,
    description: "LinnStrument User Firmware X-input smoothing strength (0 = off, 100 = strongest)",
  },
  {
    key: "linnstrument_channel_allocation",
    tier: "local",
    type: "string",
    default: "single_channel",
    perController: true,
    description:
      "LinnStrument bypass channel allocation: single channel, channel per row, or channel per note",
  },
  {
    key: "midiin_scale_bend_range",
    tier: "local",
    type: "int",
    default: 48,
    perController: true,
    description: "MPE pitch bend range (semitones) used in Nearest Scale Degree mode",
  },
  {
    key: "midiin_mapping_target",
    tier: "url",
    type: "string",
    default: "hex_layout",
    description: "Input mode: MIDI to Hex Layout or MIDI to Nearest Scale Degree",
  },
  {
    key: "hakenaudio_x_glide_shaping",
    tier: "local",
    type: "int",
    default: 100,
    perController: true,
    description: "Haken Continuum Nearest Scale Degree X-glide shaping (0 linear, 100 near-stable)",
  },
  {
    key: "hakenaudio_x_glide_mode",
    tier: "local",
    type: "string",
    default: "pitch_bending",
    perController: true,
    description:
      "Haken Continuum X glide mode: Rastered Attack + Pitch Bend (pitch_bending) or Rastered Notes (raster_to_notes)",
  },
  {
    key: "hakenaudio_glide_flip_cc",
    tier: "local",
    type: "int",
    default: 67,
    perController: true,
    description: "Haken Continuum CC number used for momentary X-glide mode flip",
  },
  {
    key: "hakenaudio_pressure_velocity",
    tier: "local",
    type: "int",
    default: 64,
    perController: true,
    description:
      "Haken Continuum Rastered Notes pressure-to-velocity blend (0 keep attack velocity, 127 follow pressure)",
  },
  {
    key: "hakenaudio_note_off_delay",
    tier: "local",
    type: "int",
    default: 45,
    perController: true,
    description: "Haken Continuum Rastered Notes auto-generated note-off delay in milliseconds",
  },
  {
    key: "hakenaudio_raster_throttle_ms",
    tier: "local",
    type: "int",
    default: 35,
    perController: true,
    description: "Haken Continuum Rastered Notes minimum retrigger interval in milliseconds",
  },
  {
    key: "hakenaudio_raster_stability",
    tier: "local",
    type: "int",
    default: 50,
    perController: true,
    description:
      "Haken Continuum Rastered Notes hysteresis amount to reduce note flutter near boundaries",
  },
  {
    key: "hakenaudio_raster_filter_mode",
    tier: "local",
    type: "string",
    default: "all",
    perController: true,
    description: "Haken Continuum raster filter mode: all degrees or selected degree subset",
  },
  {
    key: "hakenaudio_raster_filter",
    tier: "local",
    type: "string",
    default: "",
    perController: true,
    description:
      "Comma-separated scale degrees allowed for Continuum rastered attacks and Rastered Notes retriggering",
  },
  {
    key: "hakenaudio_raster_filter_snapshots",
    tier: "local",
    type: "bool",
    default: false,
    perController: true,
    description: "Show auto-generated Continuum raster filters derived from captured snapshots",
  },
  {
    key: "hakenaudio_apply_raster_in_pitch_bending",
    tier: "local",
    type: "bool",
    default: false,
    perController: true,
    description:
      "Apply the selected Continuum raster filter to attacks in Rastered Attack + Pitch Bend mode",
  },
  {
    key: "hakenaudio_shape_x_glide_to_raster",
    tier: "local",
    type: "bool",
    default: false,
    perController: true,
    description:
      "Shape Continuum pitch bending around the selected raster filter degrees instead of every scale degree",
  },
  {
    key: "midi_passthrough",
    tier: "url",
    type: "bool",
    default: false,
    description: "Sequential (passthrough) mode for the active controller",
  },
  {
    key: "tonalplexus_input_mode",
    tier: "local",
    type: "string",
    default: "layout_205",
    perController: true,
    description: "Tonal Plexus input interpretation for this controller",
  },

  // ── MIDI input ───────────────────────────────────────────────────────────────
  { key: "midiin_device", tier: "session", type: "string", default: "OFF" },
  { key: "midiin_controller_override", tier: "url", type: "string", default: "auto" },
  { key: "midiin_steps_per_channel", tier: "url", type: "int", default: 0 },
  { key: "midiin_channel_group_size", tier: "url", type: "int", default: 1 },
  { key: "midiin_anchor_channel", tier: "url", type: "int", default: 1 },
  { key: "midiin_anchor_note", tier: "url", type: "int", default: 60 },
  { key: "controller_anchor_note", tier: "session", type: "int", default: null },
  { key: "midiin_channel_legacy", tier: "url", type: "bool", default: false },
  // midi_passthrough is URL-scoped so walkthrough links can switch between
  // geometry and bypass states directly.
  // Input runtime mode keys
  // midiin_mpe_input, midiin_bend_range, midiin_bend_flip are 'local' tier (see above).
  // MPE input voice channel range. Channels 1 and 16 are typically reserved
  // (manager/global channel per MPE spec), so the default voice range is 2–15.
  { key: "midiin_mpe_lo_ch", tier: "session", type: "int", default: 2 },
  { key: "midiin_mpe_hi_ch", tier: "session", type: "int", default: 15 },
  { key: "midiin_scale_tolerance", tier: "session", type: "int", default: 25 },
  { key: "midiin_scale_fallback", tier: "session", type: "string", default: "accept" },
  { key: "midiin_pitchbend_mode", tier: "session", type: "string", default: "recency" },
  { key: "midiin_pressure_mode", tier: "session", type: "string", default: "all" },
  {
    key: "midiin_modwheel_value",
    tier: "session",
    type: "int",
    default: 0,
    description: "Last known CC1 mod-wheel value for the active MIDI input, restored on refresh",
  },
  {
    key: "midiin_modwheel_source",
    tier: "session",
    type: "string",
    default: "",
    description: "MIDI input device id that the restored mod-wheel value belongs to",
  },

  // ── Controller anchors (hardware-scoped, survive device disconnect) ──────────
  // These are keyed dynamically as "${controllerId}_anchor" in localStorage.
  // Listed here as a documentation anchor for the local tier; not used directly
  // by the registry loop since the key name is runtime-derived.
  // { key: '${controllerId}_anchor',         tier: 'local', type: 'int' },
  // { key: '${controllerId}_anchor_channel', tier: 'local', type: 'int' },

  // ── Sample synth ─────────────────────────────────────────────────────────────
  { key: "output_sample", tier: "url", type: "bool", default: true },
  { key: "instrument", tier: "url", type: "string", default: "WMRIByzantineST" },

  // ── MTS real-time output ──────────────────────────────────────────────────────
  { key: "output_mts", tier: "session", type: "bool", default: false },
  { key: "midi_device", tier: "session", type: "string", default: "OFF" },
  { key: "midi_channel", tier: "session", type: "int", default: 0 },
  { key: "midi_mapping", tier: "session", type: "string", default: "MTS1" },
  { key: "midi_velocity", tier: "session", type: "int", default: 72 },
  { key: "sysex_auto", tier: "session", type: "bool", default: false },
  { key: "sysex_type", tier: "session", type: "int", default: 126 },
  { key: "device_id", tier: "session", type: "int", default: 127 },
  { key: "tuning_map_number", tier: "session", type: "int", default: 0 },

  // ── MTS bulk dump output (dynamic + static) ───────────────────────────────────
  { key: "output_mts_bulk", tier: "session", type: "bool", default: false },
  { key: "mts_bulk_device", tier: "session", type: "string", default: "OFF" },
  { key: "mts_bulk_mode", tier: "session", type: "string", default: "dynamic" },
  { key: "mts_bulk_channel", tier: "session", type: "int", default: -1 },
  { key: "mts_bulk_sysex_auto", tier: "session", type: "bool", default: false },
  { key: "mts_bulk_device_id", tier: "session", type: "int", default: 127 },
  { key: "mts_bulk_tuning_map_number", tier: "session", type: "int", default: 0 },
  { key: "mts_bulk_tuning_map_name", tier: "session", type: "string", default: null },

  // ── MPE output ────────────────────────────────────────────────────────────────
  { key: "output_mpe", tier: "session", type: "bool", default: false },
  { key: "mpe_device", tier: "session", type: "string", default: "OFF" },
  { key: "midiin_mpe_manager_ch", tier: "session", type: "string", default: "1" },
  { key: "mpe_lo_ch", tier: "session", type: "int", default: 2 },
  { key: "mpe_hi_ch", tier: "session", type: "int", default: 8 },
  { key: "mpe_mode", tier: "session", type: "string", default: "Ableton_workaround" },
  { key: "mpe_pitchbend_range", tier: "session", type: "int", default: 48 },
  { key: "mpe_pitchbend_range_manager", tier: "session", type: "int", default: 2 },
  { key: "mpe_plus_output", tier: "session", type: "bool", default: false },
  { key: "mpe_auto_generate_yz", tier: "session", type: "bool", default: false },
  { key: "mpe_eagan_modwheel_brightness", tier: "session", type: "bool", default: false },
  { key: "mpe_eagan_brightness", tier: "session", type: "int", default: 64 },
  { key: "mpe_eagan_tilt_eq", tier: "session", type: "int", default: 64 },
  { key: "mpe_eagan_pre_level", tier: "session", type: "int", default: 64 },
  { key: "mpe_eagan_post_level", tier: "session", type: "int", default: 64 },

  // ── FluidSynth / OSC mirror ───────────────────────────────────────────────────
  {
    key: "fluidsynth_out_port",
    tier: "session",
    type: "string",
    default: null,
    perController: false,
    description: "Manual output port override for FluidSynth (null = auto-detect by name)",
  },
  { key: "fluidsynth_device", tier: "session", type: "string", default: "" },
  { key: "fluidsynth_channel", tier: "session", type: "int", default: -1 },
  { key: "output_osc", tier: "session", type: "bool", default: false },
  { key: "osc_bridge_url", tier: "session", type: "string", default: "ws://localhost:8089" },
  // OSC layer volumes — written by onOscLayerVolumeChange in hooks/use-synth-wiring.js
  // via both setSettings (so deriveOscVolumes reads the live value on every
  // in-session rebuild) and localStorage (so values survive page reload and
  // are read back via CROSS_CONTROLLER_ENTRIES in settings/session-defaults.js).
  // tier: 'local', perController: false — plain key, cross-controller.
  { key: "osc_volume_pluck", tier: "local", type: "float", default: 0.5, perController: false },
  { key: "osc_volume_buzz", tier: "local", type: "float", default: 0.5, perController: false },
  { key: "osc_volume_formant", tier: "local", type: "float", default: 0.5, perController: false },
  { key: "osc_volume_saw", tier: "local", type: "float", default: 0.5, perController: false },
  { key: "osc_quick_release", tier: "local", type: "float", default: 0.5, perController: false },
  {
    key: "osc_quick_release_time",
    tier: "local",
    type: "float",
    default: 0.25,
    perController: false,
  },
  {
    key: "osc_quick_release_raster_only",
    tier: "local",
    type: "bool",
    default: true,
    perController: false,
  },
  {
    key: "osc_sustain_buzz_formant",
    tier: "local",
    type: "bool",
    default: false,
    perController: false,
  },
  {
    key: "osc_retrigger_buzz_formant",
    tier: "local",
    type: "bool",
    default: false,
    perController: false,
  },
  // WebMIDI permission/access level restored on refresh so the explicit
  // Enable MIDI / Enable Sysex checkboxes stay in sync with device menus.
  // This is session-scoped runtime state, not a shareable preset value.
  { key: "webmidi_enabled", tier: "session", type: "bool", default: false },
  { key: "webmidi_sysex_enabled", tier: "session", type: "bool", default: false },
  { key: "webmidi_access", tier: "session", type: "string", default: "none" },

  // ── Controller geometry (runtime-derived, never stored) ───────────────────────
  // These are computed from the detected controller at startup; persisting them
  // would cause stale values if the user swaps controllers between sessions.
  { key: "axis49_center_note", tier: "runtime", type: "int", default: 53 },
  { key: "wheel_to_recent", tier: "session", type: "bool", default: true },
  { key: "wheel_scale_aware", tier: "session", type: "bool", default: false },
  { key: "midi_wheel_semitones", tier: "session", type: "int", default: 2 },

  // ── LED sync ─────────────────────────────────────────────────────────
  {
    key: "hakenaudio_out_port",
    tier: "session",
    type: "string",
    default: null,
    perController: false,
    description: "Manual output port override for Haken Continuum (null = auto-detect by name)",
  },
  {
    key: "lumatone_out_port",
    tier: "session",
    type: "string",
    default: null,
    perController: false,
    description: "Manual output port override for Lumatone (null = auto-detect by name)",
  },
  {
    key: "exquis_out_port",
    tier: "session",
    type: "string",
    default: null,
    perController: false,
    description: "Manual output port override for Exquis (null = auto-detect by name)",
  },
  {
    key: "linnstrument_out_port",
    tier: "session",
    type: "string",
    default: null,
    perController: false,
    description: "Manual output port override for LinnStrument (null = auto-detect by name)",
  },
  {
    key: "lumatone_led_sync",
    tier: "local",
    type: "bool",
    default: false,
    perController: false,
    description: "Auto Send Colours to Lumatone LEDs when scale changes",
  },
  {
    key: "lumatone_degree_filter_mode",
    tier: "local",
    type: "string",
    default: "all",
    perController: false,
    description: "Lumatone LED colour filter mode: all degrees or selected degree subset",
  },
  {
    key: "lumatone_degree_filter",
    tier: "local",
    type: "string",
    default: "",
    perController: false,
    description:
      "Comma-separated scale degrees that remain lit on the Lumatone when degree filtering is active",
  },
  {
    key: "lumatone_degree_filter_snapshots",
    tier: "local",
    type: "bool",
    default: false,
    perController: false,
    description: "Show auto-generated Lumatone colour filters derived from captured snapshots",
  },
  {
    key: "exquis_led_sync",
    tier: "local",
    type: "bool",
    default: true,
    perController: false,
    description: "Auto Send Colours to Exquis LEDs when scale changes",
  },
  {
    key: "linnstrument_led_sync",
    tier: "local",
    type: "bool",
    default: false,
    perController: false,
    description: "Auto Send Colours to LinnStrument LEDs when scale changes",
  },
  {
    key: "exquis_led_luminosity",
    tier: "local",
    type: "int",
    default: 15,
    perController: false,
    description: "Exquis LED global brightness (0–100, firmware clamps above 100)",
  },
  {
    key: "exquis_led_saturation",
    tier: "local",
    type: "float",
    default: 1.3,
    perController: false,
    description: "Exquis LED colour saturation multiplier applied in okLab space (1.0–2.5)",
  },

  // ── App preferences (localStorage, not session) ───────────────────────────────
  // hexatone_persist_on_reload is handled separately in app.jsx / hooks/use-presets.js
  // and is intentionally not part of the settings object.

  // ── Rationalisation search prefs ──────────────────────────────────────────────
  // These are stored outside the settings object, managed directly by
  // scale-table/index.js and scale-table/search-prefs.js.
  //
  // hexatone_search_prefs       (localStorage)  — JSON blob of searchPrefs state
  //                              (primeLimit, oddLimit, centsTolerance, region,
  //                              primeBounds, primeBoundsUt, existingRatios …).
  //                              Merged with DEFAULT_SEARCH_PREFS on load so new
  //                              keys are always present after updates.
  // hexatone_search_prefs_open  (sessionStorage) — "true"/"false"; keeps the panel
  //                              open across page refreshes within a session.
  // Restore Defaults clears hexatone_search_prefs and resets to DEFAULT_SEARCH_PREFS.
  //
  // There are no rationalise_* keys in the settings object. The search prefs live
  // entirely in localStorage under hexatone_search_prefs and are not URL-synced.
];

// ── Derived lookup maps ────────────────────────────────────────────────────────

/** All entries keyed by settings key for O(1) lookup. */
export const REGISTRY_BY_KEY = Object.fromEntries(
  SETTINGS_REGISTRY.map((entry) => [entry.key, entry]),
);

/** Keys that are synced via useQuery (URL + localStorage). */
export const URL_KEYS = SETTINGS_REGISTRY.filter((e) => e.tier === "url").map((e) => e.key);

/** Keys that are session-scoped (sessionStorage). */
export const SESSION_KEYS = SETTINGS_REGISTRY.filter((e) => e.tier === "session").map((e) => e.key);

/** Keys that are runtime-only (never stored). */
export const RUNTIME_KEYS = SETTINGS_REGISTRY.filter((e) => e.tier === "runtime").map((e) => e.key);

/** All local-tier entries (localStorage, not session, not URL). */
export const LOCAL_ENTRIES = SETTINGS_REGISTRY.filter((e) => e.tier === "local");

/** Local-tier entries that are scoped per-controller (key = `${ctrlId}_${key}`). */
export const PER_CONTROLLER_ENTRIES = SETTINGS_REGISTRY.filter(
  (e) => e.tier === "local" && e.perController,
);

/** Local-tier entries shared across all controllers (key = plain key). */
export const CROSS_CONTROLLER_ENTRIES = SETTINGS_REGISTRY.filter(
  (e) => e.tier === "local" && !e.perController,
);

/** Keys excluded from URL/localStorage persistence (preset-specific). */
export const PRESET_SKIP_KEYS = SETTINGS_REGISTRY.filter((e) => e.presetSkip).map((e) => e.key);

/**
 * Build the spec object for useQuery from the registry.
 *
 * Maps each 'url'-tier key to its corresponding Extract* instance.
 * The extractors are passed in by the caller so this module stays free of
 * Preact/use-query imports (pure data layer).
 *
 * @param {object} extractors  { int, float, bool, string, joined } — Extract* instances
 * @returns {object}  spec object for useQuery
 *
 * Usage in app.jsx:
 *   import { buildQuerySpec } from './persistence/settings-registry.js';
 *   import { ExtractInt, ExtractFloat, ... } from './hooks/use-query.js';
 *   const spec = buildQuerySpec({ int: ExtractInt, float: ExtractFloat,
 *                                  bool: ExtractBool, string: ExtractString,
 *                                  joined: ExtractJoinedString });
 */
/**
 * Build a flat object of default values for all url-tier and runtime-tier keys.
 * Used as the base layer in the useQuery defaults object, before the active
 * built-in tuning defaults and sessionDefaults are spread on top.
 *
 * This restores the blank-slate defaults that previously lived at the bottom of
 * settings/session-defaults.js (scale, rSteps, drSteps, hexSize, rotation, equivSteps,
 * reference_degree, center_degree, fundamental, etc.).
 */
export function buildRegistryDefaults() {
  const defaults = {};
  for (const entry of SETTINGS_REGISTRY) {
    if (entry.tier === "url" || entry.tier === "runtime" || entry.tier === "local") {
      if (entry.default !== undefined) defaults[entry.key] = entry.default;
    }
  }
  return defaults;
}

export function buildQuerySpec(extractors) {
  const spec = {};
  for (const entry of SETTINGS_REGISTRY) {
    if (entry.tier !== "url") continue;
    switch (entry.type) {
      case "int":
        spec[entry.key] = extractors.int;
        break;
      case "float":
        spec[entry.key] = extractors.float;
        break;
      case "bool":
        spec[entry.key] = extractors.bool;
        break;
      case "string":
        spec[entry.key] = extractors.string;
        break;
      case "joined":
        spec[entry.key] = extractors.joined;
        break;
    }
  }
  return spec;
}
